import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useGetNpcPersonasQuery,
  useGetNpcProgressSummaryQuery,
  useGetNpcLevelsQuery,
  PersonaWithLevelSummary,
  DifficultyTier,
  LevelWithProgress,
} from '../store/api/npcApi';
import { asArray } from '../store/api/npcResponses';
import { levelConversationUrl } from '../utils/flowLinks';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { useGetCapabilitiesQuery } from '../store/api/capabilitiesApi';

/** Engine off: no conversation-based level attempt; Record & Review still attempts levels. */
export const LEVEL_RECORD_INSTEAD_TITLE =
  'Level attempts in a conversation are not available right now. Attempt this level with Record & Review.';

const TIER_COLORS: Record<DifficultyTier, { bg: string; text: string; badge: string }> = {
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700', badge: 'bg-amber-500' },
  silver: { bg: 'bg-slate-100', text: 'text-slate-700', badge: 'bg-slate-400' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', badge: 'bg-yellow-500' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700', badge: 'bg-purple-500' },
  master: { bg: 'bg-red-100', text: 'text-red-700', badge: 'bg-red-600' },
};

const TIER_ICONS: Record<DifficultyTier, string> = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  platinum: '💎',
  master: '👑',
};

const TIER_ORDER: DifficultyTier[] = ['bronze', 'silver', 'gold', 'platinum', 'master'];

export interface PersonaMastery {
  total: number;
  mastered: number;
  byTier: Record<DifficultyTier, { total: number; mastered: number }>;
}

/**
 * Persona mastery (every level of the persona passed) from the persona list's
 * per-persona totalLevels/passedLevels. null when the list carries no level
 * progress (nothing loaded, or a response without it) — unknown, not 0 / 0.
 */
export function personaMastery(personas: PersonaWithLevelSummary[]): PersonaMastery | null {
  if (personas.length === 0) return null;
  if (!personas.every((p) => typeof p.totalLevels === 'number' && typeof p.passedLevels === 'number')) return null;
  const byTier = Object.fromEntries(TIER_ORDER.map((t) => [t, { total: 0, mastered: 0 }])) as PersonaMastery['byTier'];
  let mastered = 0;
  for (const p of personas) {
    const isMastered = p.totalLevels > 0 && p.passedLevels >= p.totalLevels;
    if (isMastered) mastered++;
    const tier = byTier[p.difficultyTier];
    if (tier) {
      tier.total++;
      if (isMastered) tier.mastered++;
    }
  }
  return { total: personas.length, mastered, byTier };
}

export function NpcProgressPage() {
  const [expandedPersonaId, setExpandedPersonaId] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<DifficultyTier | ''>('');

  // Every persona, with the caller's per-persona level progress; the tier
  // filter is applied here so persona mastery (overall and per tier) is
  // always counted over the whole roster, not the filtered slice.
  const {
    data: personasData,
    isLoading: isLoadingPersonas,
    isError: personasFailed,
    error: personasError,
    refetch: refetchPersonas,
  } = useGetNpcPersonasQuery({});
  const { data: progressSummary, isLoading: isLoadingSummary } = useGetNpcProgressSummaryQuery();
  const { data: expandedLevelsData, isLoading: isLoadingLevels } = useGetNpcLevelsQuery(
    expandedPersonaId || '',
    { skip: !expandedPersonaId }
  );
  // A "Start level" conversation needs the canonical engine: the legacy
  // runtime binds no level and scores nothing. Unloaded or unreadable: not offered.
  const { data: capabilities, isSuccess: capabilitiesLoaded } = useGetCapabilitiesQuery();
  const conversationLevelsAvailable = capabilitiesLoaded && capabilities?.conversation?.engine === true;
  const allPersonas = asArray<PersonaWithLevelSummary>(personasData);
  const personas = tierFilter ? allPersonas.filter((p) => p.difficultyTier === tierFilter) : allPersonas;
  const expandedLevels = asArray<LevelWithProgress>(expandedLevelsData);
  const mastery = personaMastery(allPersonas);

  const isLoading = isLoadingPersonas || isLoadingSummary;

  // Group personas by tier
  const personasByTier = personas.reduce((acc, persona) => {
    if (!acc[persona.difficultyTier]) {
      acc[persona.difficultyTier] = [];
    }
    acc[persona.difficultyTier].push(persona);
    return acc;
  }, {} as Record<DifficultyTier, PersonaWithLevelSummary[]>);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">NPC Progress</h1>
          <p className="mt-1 text-gray-500">Track your progress against challenging NPC personas</p>
        </div>
        <div className="flex items-center gap-3">
          {/* R0.1: the former battle-branded CTA here deep-linked into the
              incomplete live-voice path and routed ordinary users straight to it.
              Removed rather than re-pointed, because the link beside it already led
              to exactly this destination — keeping both would be duplicate UI. */}
          <Link
            to="/practice?mode=npc"
            className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <MicIcon className="w-4 h-4" />
            Start NPC Practice
          </Link>
        </div>
      </div>

      {/* Overall Stats: XP and levels from the server summary; persona mastery
          from the roster (only when it loaded — never a 0 / 0 stand-in). */}
      {(progressSummary || mastery) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {progressSummary && (
            <StatCard
              label="Total XP Earned"
              value={progressSummary.totalXpEarned.toLocaleString()}
              icon="⚡"
            />
          )}
          {progressSummary && (
            <StatCard
              label="Levels Completed"
              value={progressSummary.totalLevelsPassed.toString()}
              icon="✅"
            />
          )}
          {mastery && (
            <StatCard
              label="Personas Mastered"
              value={`${mastery.mastered} / ${mastery.total}`}
              icon="🏆"
            />
          )}
          {mastery && (
            <StatCard
              label="Overall Progress"
              value={`${mastery.total > 0 ? Math.round((mastery.mastered / mastery.total) * 100) : 0}%`}
              icon="📈"
            />
          )}
        </div>
      )}

      {/* Tier Progress */}
      {mastery && (
        <div className="card">
          <div className="card-header">
            <h2 className="font-semibold text-gray-900">Progress by Difficulty</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              {TIER_ORDER.map((tier) => {
                const tierData = mastery.byTier[tier];
                const percent = tierData.total > 0
                  ? Math.round((tierData.mastered / tierData.total) * 100)
                  : 0;
                const colors = TIER_COLORS[tier];

                return (
                  <button
                    key={tier}
                    onClick={() => setTierFilter(tierFilter === tier ? '' : tier)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      tierFilter === tier
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <span className="text-2xl">{TIER_ICONS[tier]}</span>
                      <div className={`font-medium capitalize mt-1 ${colors.text}`}>{tier}</div>
                      <div className="text-lg font-bold text-gray-900 mt-1">
                        {tierData.mastered} / {tierData.total}
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
                        <div
                          className={`h-full transition-all ${colors.badge}`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Filter indicator */}
      {tierFilter && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Showing:</span>
          <span className={`px-2 py-1 rounded-full capitalize ${TIER_COLORS[tierFilter].bg} ${TIER_COLORS[tierFilter].text}`}>
            {TIER_ICONS[tierFilter]} {tierFilter}
          </span>
          <button
            onClick={() => setTierFilter('')}
            className="text-primary-600 hover:text-primary-700"
          >
            Clear filter
          </button>
        </div>
      )}

      {/* Personas List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
        </div>
      ) : personasFailed ? (
        // A failed load is not "No personas found".
        <LoadErrorNotice
          error={personasError}
          onRetry={() => void refetchPersonas()}
          failedMessage="Your NPC personas could not be loaded."
          testIdPrefix="npc-personas"
        />
      ) : personas.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          No personas found. Check back later for new challenges!
        </div>
      ) : (
        <div className="space-y-4">
          {tierFilter ? (
            // Show filtered personas in a flat list
            <div className="space-y-3">
              {personas.map((persona) => (
                <PersonaProgressCard
                  key={persona.id}
                  persona={persona}
                  isExpanded={expandedPersonaId === persona.id}
                  onToggle={() => setExpandedPersonaId(
                    expandedPersonaId === persona.id ? null : persona.id
                  )}
                  levels={expandedPersonaId === persona.id ? expandedLevels : []}
                  isLoadingLevels={isLoadingLevels && expandedPersonaId === persona.id}
                  conversationLevelsAvailable={conversationLevelsAvailable}
                />
              ))}
            </div>
          ) : (
            // Show personas grouped by tier
            TIER_ORDER.map((tier) => {
              const tierPersonas = personasByTier[tier];
              if (!tierPersonas || tierPersonas.length === 0) return null;

              return (
                <div key={tier} className="card">
                  <div className="card-header flex items-center gap-2">
                    <span className="text-xl">{TIER_ICONS[tier]}</span>
                    <h2 className={`font-semibold capitalize ${TIER_COLORS[tier].text}`}>
                      {tier} Tier
                    </h2>
                    <span className="text-sm text-gray-500">
                      ({tierPersonas.length} personas)
                    </span>
                  </div>
                  <div className="card-body space-y-3">
                    {tierPersonas.map((persona) => (
                      <PersonaProgressCard
                        key={persona.id}
                        persona={persona}
                        isExpanded={expandedPersonaId === persona.id}
                        onToggle={() => setExpandedPersonaId(
                          expandedPersonaId === persona.id ? null : persona.id
                        )}
                        levels={expandedPersonaId === persona.id ? expandedLevels : []}
                        isLoadingLevels={isLoadingLevels && expandedPersonaId === persona.id}
                        conversationLevelsAvailable={conversationLevelsAvailable}
                      />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="card">
      <div className="card-body flex items-center gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{label}</div>
        </div>
      </div>
    </div>
  );
}

interface PersonaProgressCardProps {
  persona: PersonaWithLevelSummary;
  isExpanded: boolean;
  onToggle: () => void;
  levels: LevelWithProgress[];
  isLoadingLevels: boolean;
  /** The server's conversation engine is on: levels can be attempted in a conversation. */
  conversationLevelsAvailable: boolean;
}

function PersonaProgressCard({
  persona,
  isExpanded,
  onToggle,
  levels,
  isLoadingLevels,
  conversationLevelsAvailable,
}: PersonaProgressCardProps) {
  const tierColors = TIER_COLORS[persona.difficultyTier];
  const tierIcon = TIER_ICONS[persona.difficultyTier];
  const progressPercent = persona.totalLevels > 0
    ? Math.round((persona.passedLevels / persona.totalLevels) * 100)
    : 0;
  const isMastered = persona.passedLevels === persona.totalLevels && persona.totalLevels > 0;

  return (
    <div className={`border rounded-lg overflow-hidden ${isMastered ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
      <button
        onClick={onToggle}
        className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-colors"
      >
        {/* Avatar */}
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${tierColors.bg}`}>
          {persona.avatarUrl ? (
            <img src={persona.avatarUrl} alt={persona.label} className="w-12 h-12 rounded-full object-cover" />
          ) : (
            <span className="text-2xl">{tierIcon}</span>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{persona.label}</span>
            {isMastered && (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                Mastered
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 truncate">{persona.description}</p>
        </div>

        {/* Progress */}
        <div className="flex-shrink-0 w-32 hidden sm:block">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
            <span>{persona.passedLevels} / {persona.totalLevels}</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all ${isMastered ? 'bg-green-500' : tierColors.badge}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Expand icon */}
        <div className="flex-shrink-0">
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Levels */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          {isLoadingLevels ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full" />
            </div>
          ) : levels.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No levels available yet</p>
          ) : (
            <div className="space-y-2">
              {levels.map((level) => (
                <LevelProgressRow
                  key={level.id}
                  level={level}
                  personaKey={persona.key}
                  personaId={persona.id}
                  conversationAvailable={conversationLevelsAvailable}
                />
              ))}
            </div>
          )}

          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center gap-3 flex-wrap">
            {/* R0.1: the former battle-branded CTA is removed for the same reason as
                the header one — the adjacent link already targeted this exact route.
                The survivor is retitled from "Text Practice", which misdescribed the
                classic record & review flow, to wording that names the persona
                context it actually preserves. */}
            <Link
              to={`/practice?mode=npc&persona=${persona.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors text-sm"
            >
              <MicIcon className="w-4 h-4" />
              Practice with This Persona
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export function LevelProgressRow({
  level, personaKey, personaId, conversationAvailable,
}: { level: LevelWithProgress; personaKey: string; personaId: string; conversationAvailable: boolean }) {
  const isPassed = level.progress?.passed;
  const bestScore = level.progress?.bestOverallScore ?? null;

  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${
      isPassed ? 'bg-green-100' : level.isLocked ? 'bg-gray-100 opacity-60' : 'bg-white'
    }`}>
      {/* Level indicator */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
        isPassed
          ? 'bg-green-500 text-white'
          : level.isLocked
          ? 'bg-gray-300 text-gray-500'
          : 'bg-primary-100 text-primary-700'
      }`}>
        {isPassed ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : level.isLocked ? (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        ) : (
          level.levelNumber
        )}
      </div>

      {/* Level info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 text-sm">{level.displayName}</div>
        <div className="text-xs text-gray-500">
          Min: {level.minOverallScore}% • +{level.rewardXp} XP
        </div>
      </div>

      {/* Score */}
      {level.progress && level.progress.attempts > 0 && (
        <div className="text-right flex-shrink-0">
          <div className={`font-bold ${
            isPassed ? 'text-green-700' : bestScore !== null && bestScore >= level.minOverallScore ? 'text-green-600' : 'text-gray-600'
          }`}>
            {bestScore === null ? 'Not measured' : `${Math.round(bestScore)}%`}
          </div>
          <div className="text-xs text-gray-500">{level.progress.attempts} tries</div>
        </div>
      )}

      {/* Locked indicator */}
      {level.isLocked && !level.progress && (
        <div className="text-xs text-gray-400">Locked</div>
      )}

      {/* Start: a canonical conversation bound to this level (Written Chat, or
          Live Conversation when the server's realtime capability is on) — only
          when the server's conversation engine is on. A locked level offers no
          Start — the server would refuse it anyway. */}
      {!level.isLocked && conversationAvailable && (
        <Link
          to={levelConversationUrl(personaKey, level.id)}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
        >
          {isPassed ? 'Replay level' : 'Start level'}
        </Link>
      )}
      {/* Engine off: a conversation would ignore the level, so none is
          offered. Record & Review attempts the level for real. */}
      {!level.isLocked && !conversationAvailable && (
        <Link
          to={`/practice?mode=npc&persona=${personaId}`}
          title={LEVEL_RECORD_INSTEAD_TITLE}
          aria-label={`${isPassed ? 'Replay' : 'Attempt'} ${level.displayName} with Record & Review`}
          data-testid="level-record-instead"
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary-600 text-primary-700 hover:bg-primary-50"
        >
          Record &amp; Review
        </Link>
      )}
    </div>
  );
}

function MicIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
      />
    </svg>
  );
}

export default NpcProgressPage;
